import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { getUserPlan } from "../../utils/planAccess";

/**
 * One small "your plan" card for /, /home and /journey. Tells the user what
 * their plan opens and offers the single next step; Global users see nothing.
 */
const PlanBanner: React.FC<{ className?: string }> = ({ className = "" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!user) return null;
  const plan = getUserPlan(user);
  if (plan === "global") return null;

  const isFree = plan === "free";
  const title = isFree
    ? t("astria.bannerFreeTitle", "You're on Free")
    : t("astria.bannerProTitle", "You're on Pro");
  const text = isFree
    ? t(
        "astria.bannerFreeText",
        "Pro opens every tab in your region. Global opens every region.",
      )
    : t(
        "astria.bannerProText",
        "Your region is unlimited. Global adds every other region.",
      );
  const cta = isFree
    ? t("astria.bannerFreeCta", "See plans")
    : t("astria.bannerProCta", "Get Global");

  return (
    <div
      className={`mx-auto flex w-full max-w-[560px] items-center gap-4 rounded-2xl px-4 py-3.5 ${className}`}
      style={{
        background: "var(--astria-surface)",
        border: "1px solid rgba(167,139,255,0.25)",
      }}
      role="region"
      aria-label={title as string}
    >
      <Sparkles size={20} aria-hidden="true" style={{ color: "var(--astria-accent-primary)", flexShrink: 0 }} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold" style={{ color: "var(--astria-text-primary)" }}>
          {title}
        </p>
        <p className="mt-0.5 text-xs leading-snug" style={{ color: "var(--astria-text-secondary)" }}>
          {text}
        </p>
      </div>
      <button
        type="button"
        onClick={() => navigate("/account/plans-billing")}
        className="flex-shrink-0 rounded-full px-4 py-2 text-xs font-medium text-white"
        style={{ background: "linear-gradient(135deg, #d9cfff, #a78bff)" }}
      >
        {cta}
      </button>
    </div>
  );
};

export default PlanBanner;
